import { useEffect, useMemo, useState } from 'react'

import { ENTITY_TYPES } from '@sales-center/common'
import { toast } from '@sales-center/components'

import { getSalesTeamPositionsData } from '@user/store/authorization/api'
import { getBranchList } from '@user/store/branches/api'
import { getCampaigns } from '@user/store/campaigns/api'
import { getAllCustomFields } from '@user/store/customFields/api'
import { getDivisions } from '@user/store/divisions/api'
import { getAllEntityFormSettings } from '@user/store/entityFormSettings/api'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { getLocationTypes } from '@user/store/locationTypes/api'
import {
  getAllFunnelStages,
  getSalesFunnelList,
} from '@user/store/salesFunnel/api'
import { getSalesTeamList } from '@user/store/salesTeam/api'
import { getSources } from '@user/store/sources/api'
import { getTargetPests } from '@user/store/targetPests/api'

import type { AdditionalMissingKey, MissingFieldGroup } from '../typings'

type Lookup =
  | 'branches'
  | 'salesTeams'
  | 'positions'
  | 'salesFunnels'
  | 'funnelStages'
  | 'divisions'
  | 'locationTypes'
  | 'targetPests'
  | 'sources'
  | 'campaigns'
  | 'customFields'
  | 'formSettings'

const ADDITIONAL_LOOKUPS: Partial<Record<AdditionalMissingKey, Lookup[]>> = {
  divisionId: ['divisions'],
  locationTypeId: ['locationTypes'],
  targetPests: ['targetPests'],
  leadSourceId: ['sources'],
  campaignId: ['campaigns'],
}

const LOOKUP_ERROR = 'Unable to load the data needed to update this lead.'

/** The lookups the rendered groups read from the store; nothing else is fetched. */
function getRequiredLookups(groups: MissingFieldGroup[]): Lookup[] {
  const required = new Set<Lookup>()

  groups.forEach((group) => {
    switch (group.kind) {
      case 'nameOrCompany':
        required.add('formSettings')
        break
      case 'branchId':
        required.add('branches')
        break
      case 'assignment':
        group.fields.forEach(({ fieldKey }) => {
          if (fieldKey === 'salesTeamId' || fieldKey === 'assigneeId') {
            required.add('salesTeams')
            required.add('positions')
          }
          if (fieldKey === 'salesFunnelId') required.add('salesFunnels')
          if (fieldKey === 'salesFunnelStageId') {
            required.add('salesFunnels')
            required.add('funnelStages')
          }
        })
        break
      case 'additional':
        ADDITIONAL_LOOKUPS[group.key]?.forEach((lookup) => required.add(lookup))
        break
      case 'custom':
        required.add('customFields')
        break
      default:
        break
    }
  })

  return [...required].sort()
}

/**
 * Loads only the store data the modal's inputs need for the fields the server reported missing,
 * instead of the full Add Lead set. Returns whether any of it is still loading.
 */
export function useLoadLookups(groups: MissingFieldGroup[]) {
  const dispatch = useAppDispatch()

  const key = useMemo(() => getRequiredLookups(groups).join(','), [groups])
  const [loadedKey, setLoadedKey] = useState<string | null>(null)

  useEffect(() => {
    let mounted = true
    const required = new Set(key ? key.split(',') : [])

    const requests = [
      required.has('branches') && dispatch(getBranchList()),
      required.has('salesTeams') &&
        dispatch(getSalesTeamList({ includeTeamsByBranchId: true })),
      required.has('positions') && dispatch(getSalesTeamPositionsData()),
      required.has('salesFunnels') && dispatch(getSalesFunnelList(false)),
      required.has('funnelStages') && dispatch(getAllFunnelStages()),
      required.has('divisions') && dispatch(getDivisions()),
      required.has('locationTypes') && dispatch(getLocationTypes()),
      required.has('targetPests') && dispatch(getTargetPests()),
      required.has('sources') && dispatch(getSources()),
      required.has('campaigns') && dispatch(getCampaigns()),
      required.has('customFields') && dispatch(getAllCustomFields()),
      required.has('formSettings') &&
        dispatch(getAllEntityFormSettings(ENTITY_TYPES.Lead)),
    ].filter((request) => request !== false)

    Promise.all(requests).then((results) => {
      if (!mounted) return
      if (results.some((result) => result.meta.requestStatus === 'rejected'))
        toast.error(LOOKUP_ERROR)
      setLoadedKey(key)
    })

    return () => {
      mounted = false
    }
  }, [dispatch, key])

  return loadedKey !== key
}
